import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import ElliottFace from '../../../../components/ElliottFace';

interface TalkingFaceProps {
  size?: number;
  /** Voice is playing — drive the talking animation. */
  speaking: boolean;
  /** Live speech amplitude 0..1 (server-TTS analyser), read per frame. */
  levelRef?: React.MutableRefObject<number>;
}

// Elliott's face actually TALKING: the mouth opens with the audio amplitude
// and the head swells/glows on syllables, sitting still in the pauses.
// Real levels come from the server-TTS analyser; the browser voice exposes no
// audio stream, so after ~400ms without data a synthetic syllable cadence
// keeps the mouth moving while speech is active. Animation is driven outside
// React (rAF + inline styles) so the 60fps loop never re-renders the page.
const TalkingFace: React.FC<TalkingFaceProps> = ({ size = 150, speaking, levelRef }) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const smile = wrap.querySelector('.elliott-smile') as SVGPathElement | null;
    if (smile) {
      smile.style.transformBox = 'fill-box';
      smile.style.transformOrigin = 'center';
    }
    const reset = () => {
      wrap.style.transform = '';
      wrap.style.boxShadow = '';
      if (smile) smile.style.transform = '';
    };
    if (!speaking) {
      reset();
      return undefined;
    }
    let raf = 0;
    let smooth = 0;
    let lastRealAt = 0;
    const tick = () => {
      const now = performance.now();
      const real = levelRef?.current ?? 0;
      if (real > 0.02) lastRealAt = now;
      // Two overlapping sines ≈ syllable rhythm when there is no analyser data.
      const target = now - lastRealAt < 400 ? real : 0.3 + 0.22 * Math.sin(now / 92) + 0.14 * Math.sin(now / 37);
      smooth += (Math.max(0, Math.min(1, target)) - smooth) * 0.3;
      wrap.style.transform = `scale(${1 + smooth * 0.07})`;
      wrap.style.boxShadow = `0 0 ${10 + smooth * 28}px rgba(247,148,32,${0.35 + smooth * 0.45})`;
      if (smile) smile.style.transform = `scaleY(${0.5 + smooth})`;
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(raf);
      reset();
    };
  }, [speaking, levelRef]);

  return (
    <Box ref={wrapRef} sx={{ borderRadius: '44%', lineHeight: 0 }}>
      <ElliottFace size={size} pulse={false} glow={!speaking} />
    </Box>
  );
};

export default TalkingFace;
